import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import UserLayout from '../components/UserLayout';
import Button from '../components/Button';
import { ArrowLeftIcon, HelpCircleIcon, MessageSquareIcon, PhoneIcon, MailIcon, FileTextIcon, SearchIcon, ChevronDownIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
const SupportPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('help');
  const [searchTerm, setSearchTerm] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketType, setTicketType] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>('general');
  // Simplified for brevity - would normally contain all the FAQ data
  const faqCategories = [{
    id: 'general',
    name: 'General Questions',
    icon: <HelpCircleIcon className="h-5 w-5" data-id="element-6429" />,
    faqs: [{
      question: 'What services are available on the Edo State portal?',
      answer: 'The Edo State portal offers a wide range of services including tax payments, business registrations, land documentation, vehicle registration, and various certificate applications.'
    }, {
      question: 'How do I create an account?',
      answer: 'To create an account, click on the "Register" button on the homepage. You will need to provide your personal details, contact information, and create a password.'
    }]
  }];
  // Simplified tickets data
  const tickets = [{
    id: 'TICKET-2023-001',
    subject: 'Issue with Tax Clearance Application',
    status: 'Open',
    date: '20/12/2023',
    lastUpdate: '20/12/2023',
    category: 'Application',
    priority: 'Medium'
  }];
  const filteredFaqs = faqCategories.flatMap(category => category.faqs.filter(faq => faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase())).map(faq => ({
    ...faq,
    category: category.name
  })));
  const handleSubmitTicket = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      // Reset form
      setTicketSubject('');
      setTicketType('');
      setTicketDescription('');
      setAttachedFile(null);
    }, 1500);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };
  return <UserLayout data-id="element-6430">
      <div className="py-8 px-4 sm:px-6 lg:px-8" data-id="element-6431">
        <div className="max-w-7xl mx-auto" data-id="element-6432">
          {/* Page Header */}
          <div className="mb-8" data-id="element-6433">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-6434">Support Center</h1>
            <p className="text-gray-600 mt-1" data-id="element-6435">
              Get help with your applications, payments, and account management
            </p>
          </div>
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-6436">
            <div className="border-b border-gray-200" data-id="element-6437">
              <nav className="flex -mb-px" aria-label="Tabs" data-id="element-6438">
                <button onClick={() => setActiveTab('help')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'help' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6439">
                  <HelpCircleIcon className="h-5 w-5 mx-auto mb-1" data-id="element-6440" />
                  Help Center
                </button>
                <button onClick={() => setActiveTab('contact')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'contact' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6441">
                  <MessageSquareIcon className="h-5 w-5 mx-auto mb-1" data-id="element-6442" />
                  Contact Us
                </button>
                <button onClick={() => setActiveTab('tickets')} className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'tickets' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-6443">
                  <FileTextIcon className="h-5 w-5 mx-auto mb-1" data-id="element-6444" />
                  My Support Tickets
                </button>
              </nav>
            </div>
          </div>
          {/* Tab Content */}
          {activeTab === 'help' && <div className="space-y-6" data-id="element-6445">
              {/* Search */}
              <div className="bg-white rounded-lg shadow p-4" data-id="element-6446">
                <div className="max-w-xl mx-auto" data-id="element-6447">
                  <label htmlFor="search" className="sr-only" data-id="element-6448">
                    Search FAQs
                  </label>
                  <div className="relative" data-id="element-6449">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6450">
                      <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-6451" />
                    </div>
                    <input type="text" id="search" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search frequently asked questions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-6452" />
                  </div>
                </div>
              </div>
              {/* FAQ Content - simplified for brevity */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-6453">
                {/* Categories */}
                <div className="md:col-span-1" data-id="element-6454">
                  <div className="bg-white rounded-lg shadow" data-id="element-6455">
                    <div className="p-4 border-b border-gray-200" data-id="element-6456">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-6457">
                        Categories
                      </h2>
                    </div>
                    <nav className="p-2" data-id="element-6458">
                      {faqCategories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeCategory === category.id ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-6459">
                          <span className="mr-3 text-gray-500" data-id="element-6460">
                            {category.icon}
                          </span>
                          {category.name}
                        </button>)}
                    </nav>
                  </div>
                </div>
                {/* FAQ List */}
                <div className="md:col-span-3" data-id="element-6461">
                  <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-6462">
                    <div className="border-b border-gray-200 bg-gray-50 px-4 py-3" data-id="element-6463">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-6464">
                        {searchTerm ? 'Search Results' : activeCategory ? faqCategories.find(c => c.id === activeCategory)?.name || 'FAQs' : 'Frequently Asked Questions'}
                      </h2>
                    </div>
                    {/* FAQ content would go here - simplified for brevity */}
                    <div className="p-4" data-id="element-6465">
                      <p className="text-sm text-gray-600" data-id="element-6466">
                        Select a category to view FAQs or search for specific
                        questions.
                      </p>
                    </div>
                  </div>
                  {/* Still Need Help */}
                  <div className="mt-6 bg-green-50 border border-green-100 rounded-lg p-6 text-center" data-id="element-6467">
                    <h3 className="text-lg font-medium text-green-800 mb-2" data-id="element-6468">
                      Still Need Help?
                    </h3>
                    <p className="text-sm text-green-700 mb-4" data-id="element-6469">
                      Our support team is always ready to assist you with any
                      questions or issues.
                    </p>
                    <Button variant="primary" onClick={() => setActiveTab('contact')} data-id="element-6470">
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Contact Form - simplified for brevity */}
          {activeTab === 'contact' && <div className="bg-white rounded-lg shadow p-6" data-id="element-6471">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-6472">
                Submit a Support Ticket
              </h2>
              <p className="text-sm text-gray-500 mb-6" data-id="element-6473">
                Fill out the form below and our team will get back to you as
                soon as possible.
              </p>
              <Button variant="primary" onClick={() => setIsSubmitted(true)} data-id="element-6474">
                Submit Ticket
              </Button>
            </div>}
          {/* Tickets - simplified for brevity */}
          {activeTab === 'tickets' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-6475">
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between" data-id="element-6476">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-6477">
                  My Support Tickets
                </h2>
                <Button variant="primary" size="sm" onClick={() => setActiveTab('contact')} data-id="element-6478">
                  New Ticket
                </Button>
              </div>
              <div className="p-4" data-id="element-6479">
                <p className="text-sm text-gray-600" data-id="element-6480">
                  View and manage your support tickets here.
                </p>
              </div>
            </div>}
          {/* Quick Links */}
          <div className="mt-8 bg-white rounded-lg shadow p-6" data-id="element-6481">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6482">
              Quick Help Links
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6483">
              <Link to="/profile" className="block p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all duration-200" data-id="element-6484">
                <div className="flex items-center text-green-600 mb-2" data-id="element-6485">
                  <span className="font-medium" data-id="element-6486">Account Settings</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-6487">
                  Manage your profile and account preferences
                </p>
              </Link>
              <Link to="/payment-history" className="block p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all duration-200" data-id="element-6488">
                <div className="flex items-center text-green-600 mb-2" data-id="element-6489">
                  <span className="font-medium" data-id="element-6490">Payment Issues</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-6491">
                  Get help with payments and receipts
                </p>
              </Link>
              <Link to="/track-application" className="block p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all duration-200" data-id="element-6492">
                <div className="flex items-center text-green-600 mb-2" data-id="element-6493">
                  <span className="font-medium" data-id="element-6494">Track Applications</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-6495">
                  Check the status of your service applications
                </p>
              </Link>
              <a href="#" className="block p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all duration-200" data-id="element-6496">
                <div className="flex items-center text-green-600 mb-2" data-id="element-6497">
                  <span className="font-medium" data-id="element-6498">User Guide</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-6499">
                  Learn how to use the portal effectively
                </p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </UserLayout>;
};
export default SupportPage;